'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var mplTokenMetadata = require('@metaplex-foundation/mpl-token-metadata');
var web3_js = require('@solana/web3.js');
var Authorization = require('../Authorization.cjs');
var Operation = require('../../../types/Operation.cjs');
var Amount = require('../../../types/Amount.cjs');
var TransactionBuilder = require('../../../utils/TransactionBuilder.cjs');
var Metadata = require('../models/Metadata.cjs');

const TOKEN_AUTH_RULES_ID = new web3_js.PublicKey('auth9SigNpDKz4sJJ1DfCTuZrZNSAgh9sFD3rboVmgg');

// -----------------
// Operation
// -----------------

const Key = 'MintNftOperation';

/**
 * Mint token(s) for an NFT or SFT.
 *
 * ```ts
 * await metaplex.nfts().mint({
 *   nftOrSft,
 *   toOwner,
 *   amount: token(5),
 * });
 * ```
 *
 * @group Operations
 * @category Constructors
 */
const mintNftOperation = Operation.useOperation(Key);

/**
 * @group Operations
 * @category Types
 */

/**
 * @group Operations
 * @category Handlers
 */
const mintNftOperationHandler = {
  handle: async (operation, metaplex, scope) => {
    return mintNftBuilder(metaplex, operation.input, scope).sendAndConfirm(metaplex, scope.confirmOptions);
  }
};

// -----------------
// Builder
// -----------------

/**
 * @group Transaction Builders
 * @category Inputs
 */

/**
 * Mint token(s) for an NFT or SFT.
 *
 * ```ts
 * const transactionBuilder = metaplex
 *   .nfts()
 *   .builders()
 *   .mint({
 *     nftOrSft,
 *     toOwner,
 *     amount: token(5),
 *   });
 * ```
 *
 * @group Transaction Builders
 * @category Constructors
 */
const mintNftBuilder = (metaplex, params, options = {}) => {
  const {
    programs,
    payer = metaplex.rpc().getDefaultFeePayer()
  } = options;
  const {
    nftOrSft,
    authority = metaplex.identity(),
    authorizationDetails,
    toOwner = metaplex.identity().publicKey,
    amount = Amount.token(1)
  } = params;

  // Auth.
  const auth = Authorization.parseTokenMetadataAuthorization(metaplex, {
    mint: nftOrSft.address,
    authority: '__kind' in authority ? authority : {
      __kind: 'metadata',
      updateAuthority: authority
    },
    authorizationDetails,
    programs
  });

  // Programs.
  const tokenMetadataProgram = metaplex.programs().getTokenMetadata(programs);
  const ataProgram = metaplex.programs().getAssociatedToken(programs);
  const tokenProgram = metaplex.programs().getToken(programs);
  const systemProgram = metaplex.programs().getSystem(programs);

  // PDAs.
  const metadata = metaplex.nfts().pdas().metadata({
    mint: nftOrSft.address,
    programs
  });
  const masterEdition = metaplex.nfts().pdas().masterEdition({
    mint: nftOrSft.address,
    programs
  });

  // Destination token account.
  const toToken = params.toToken ?? metaplex.tokens().pdas().associatedTokenAccount({
    mint: nftOrSft.address,
    owner: toOwner,
    programs
  });
  return TransactionBuilder.TransactionBuilder.make().setFeePayer(payer)

  // Update the metadata account.
  .add({
    instruction: mplTokenMetadata.createMintInstruction({
      token: toToken,
      tokenOwner: toOwner,
      metadata,
      masterEdition: Metadata.isNonFungible(nftOrSft) ? masterEdition : undefined,
      tokenRecord: metaplex.nfts().pdas().tokenRecord({
        mint: nftOrSft.address,
        token: toToken,
        programs
      }),
      mint: nftOrSft.address,
      authority: auth.accounts.authority,
      // delegateRecord, // TODO: add when program supports Authority delegate.
      payer: payer.publicKey,
      systemProgram: systemProgram.address,
      sysvarInstructions: web3_js.SYSVAR_INSTRUCTIONS_PUBKEY,
      splTokenProgram: tokenProgram.address,
      splAtaProgram: ataProgram.address,
      authorizationRules: auth.accounts.authorizationRules,
      authorizationRulesProgram: TOKEN_AUTH_RULES_ID
    }, {
      mintArgs: {
        __kind: 'V1',
        amount: amount.basisPoints,
        ...auth.data
      }
    }, tokenMetadataProgram.address),
    signers: [payer, ...auth.signers],
    key: params.instructionKey ?? 'mintNft'
  });
};

exports.mintNftBuilder = mintNftBuilder;
exports.mintNftOperation = mintNftOperation;
exports.mintNftOperationHandler = mintNftOperationHandler;
//# sourceMappingURL=mintNft.cjs.map
